// Copyright (c) 2023 Visual Intelligence LP. All rights reserved.
//
import * as React from "react";
import { withStyles } from "tss-react/mui";

import "./Sheets.css";

import { SelectEventArgs, TabComponent, TabItemDirective, TabItemsDirective } from '@syncfusion/ej2-react-navigations';

import { HeaderConfig } from "@modules/Private/components/Header";
import { Field2Label } from "@shared/constants/common";
import { CURRENT_WA_TAB_INDEX } from "@shared/constants/sp-sheets";
import { mkUserKey, readFrLocalStorage } from "@shared/utils/common";
import container from "@core/di";
import AuthStore from "@core/stores/auth";
import { ROLES } from "@shared/constants/user";
import { EquipmentDTO, WASummary } from "@shared/models/windarea";
import SEquipments from "../SEquipments";
import SSummary from "../SSummary";
// import SCalculations from "../SCalculations";

// Prop interface for the main class.
export interface SheetsProps {
  className?: string;
  setHeaderConfig: (config: HeaderConfig) => any;
  name: string;                                                     // orderID.
  summaryInfo: () => Array<WASummary>;                              // Summary info
  equipmentsInfo: () => Array<EquipmentDTO>;                        // Equipments' detailed info
  checkEquipmentChanged: (boolean) => boolean;                      // Flag indicating the equipment array has changed
  towerNames: Array<string>;                                        // Tower names.
  equipmentTypes: {[key: number]: string};                          // Possible equipment types (id => name).
  onUpdateEquipments: (args: Array<EquipmentDTO>) => Promise<Array<number>>;        // Update a list of equipments
  onAddEquipment: (args: EquipmentDTO) => Promise<number>;                          // Add a new equipment
  onDeleteEquipments: (args: Array<number>) => Promise<Array<number>>;              // Delete a list of equipments
}

// Class to display the tabs containting the spreadsheets.
class Sheets extends React.Component<SheetsProps> {
  // Items in next two must be corresponding to each other.
  private tabLabels: Array<{ text: string}>;                     // Tab labels.
  private tabContents: Array<(props: SheetsProps) => any>;      // Funcs to generate tab content.

  // Store objs to query for data from portal-api.
  private authStore = container.get<AuthStore>(AuthStore.diToken);

  // static contextType = RefreshFlagContext;

  private lockedSites: string;  // List of display sites to be "locked" and "applied" across tabs

  // Constructor.
  constructor(props: SheetsProps) {
    super(props);
    this.lockedSites = '';

    const isCustomerUser = this.authStore.user.roles.every((role) => ROLES.customerUser == role);

    // Setup Labels and funcs to display the sheets.
    this.tabLabels = [];
    this.tabContents = [];

    if (!isCustomerUser) {
      this.tabLabels.push({ text: Field2Label.windAreaSummary });
      this.tabContents.push(this.makeSummarySheetContent.bind(this)); // Wind Area Calculation

      this.tabLabels.push({ text: Field2Label.windAreaEquipments });
      this.tabContents.push(this.makeEquipmentSheetContent.bind(this));  // Equipments

      // this.tabLabels.push({ text: Field2Label.windAreaCalculation });
      // this.tabContents.push(this.makeCalculationSheetContent.bind(this)); // Wind Area Calculation
    }
  }

  // Setup the component to show the wind area summary (SSummary)
  makeSummarySheetContent(props: SheetsProps) {
    const { name, summaryInfo, checkEquipmentChanged } = props;
    
    return (
      <SSummary
        name={name}
        summaryInfo={summaryInfo}
        checkEquipmentChanged={checkEquipmentChanged}
      />
    )
  }

  // Setup the component to show the equipment info (SEquipments).
  makeEquipmentSheetContent(props: SheetsProps) {
    const { name, equipmentsInfo, towerNames, equipmentTypes, onAddEquipment, onDeleteEquipments, onUpdateEquipments } = props;
    
    return (
      <SEquipments
        name={name}
        equipmentsInfo={equipmentsInfo}
        towerNames={towerNames}
        equipmentTypes={equipmentTypes}
        onUpdateEquipments={onUpdateEquipments}
        onAddEquipment={onAddEquipment}
        onDeleteEquipments={onDeleteEquipments}
      />
    )
  }

  // Setup the component to show the wind calculations (SCalculations).
  // makeCalculationSheetContent(props: SheetsProps) {
  //   const { name, equipmentsInfo, towerNames, equipmentTypes, onAddEquipment, onDeleteEquipments, onUpdateEquipments } = props;
    
  //   return (
  //     <SCalculations
  //       name={name}
  //       equipmentsInfo={equipmentsInfo}
  //       towerNames={towerNames}
  //       equipmentTypes={equipmentTypes}
  //       onUpdateEquipments={onUpdateEquipments}
  //       onAddEquipment={onAddEquipment}
  //       onDeleteEquipments={onDeleteEquipments}
  //     />
  //   )
  // }

  // Triggered after a tab being selected.
  handleTabSelected(args: SelectEventArgs) {
    // console.log('*** (Sheets) handleTabSelected:', args);
    // const { onRefresh } = this.props;

    // Retain tab selection for next session.
    localStorage.setItem(CURRENT_WA_TAB_INDEX, args.selectedIndex.toString());

    // Some subcomponent has requested a refresh.  Call parent method to refresh the page.
    // if (this.needRefresh) {
    //   onRefresh();
    //   this.needRefresh = false;
    // }

    if (args.selectedIndex == 0) {
      const authStore = container.get<AuthStore>(AuthStore.diToken);
      const gridId = mkUserKey(authStore.user.id, 'wind-area-summary');
      if (document) {
        const elem = document.getElementById(gridId);
        if (elem) {
          const gridSummary = (elem as any).ej2_instances[0];
          if (gridSummary) {
            gridSummary.load();
          }
        }
      }
    }
  }

  // Note: this hook is normally used for optimization (ie. hint for skipping a rerender).
  //       It looks like our intention here is to cause a rerender.
  // shouldComponentUpdate(/*nextProps: Readonly<SPSheetProps>, nextState: Readonly<{}>, nextContext: any*/): boolean {
  //     const { shouldUpdate } = this.props;
  //     return shouldUpdate();
  // }

  // Render the tab sheet page.
  render() {
    const classes = withStyles.getClasses(this.props);
    const initialTabIndex = Number(readFrLocalStorage(CURRENT_WA_TAB_INDEX));
    // console.log(`****** render Sheets ${new Date()}:`, sitesInformation.length);

    // Show blank page during refreshing (?) (seems related to RefreshFlagContext in Private.tsx.)
    if (this.context?.refreshFlag) {
      this.context.setRefresh(false);
      return (<></>);
    }

    // Show the tabs.  Each tab holds a subcomponent showing a grid.
    return (
      <div className={`control-pane ${classes.root}`}>
        <div className='control-section'>
          <TabComponent 
            heightAdjustMode='Auto'
            selectedItem={initialTabIndex}
            selected={this.handleTabSelected.bind(this)}
          >
            <TabItemsDirective>
              {this.tabContents.map((content, idx) => {// Render the tab contents from the array.
                return (
                  <TabItemDirective header={this.tabLabels[idx]} content={() => content(this.props)} key={this.tabLabels[idx].text}/> // use arrow func for 'content', so new props will trigger a rerender.
                )
              }
              )}
            </TabItemsDirective>
          </TabComponent>
        </div>
      </div>
    );
  }
}

const SheetsStyled = withStyles(
  Sheets, 
  (theme, props) => ({
    "root": {
      paddingTop: 25,
      width: '100%',
    },
    "header": {
      paddingTop: theme.spacing(5),
      paddingBottom: theme.spacing(5)
    },
    "table": {
      paddingTop: 25
    },
  })
);

export default SheetsStyled;